import React, { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material'
import Tree from '../../tree/Tree'
import useVariableTree from '../../../hooks/useVariableTree'
import { GenNode } from '../../../types/carbon/models/GenNode'
import { getNodeID } from '../../../utils/nodeTypes'

export interface CaseFilterPickerDialogProps {
  open: boolean
  onClose: () => void
  onSave: (syntax: string) => void
  customer: string
  job: string
}

/**
 * Picks a case filter the way Carbon accepts one (XRawTab.cs "Setup
 * CaseFilter"): one variable, not hierarchic, with the codes to keep. It
 * writes `name(c1;c2)`; Carbon reads only the codes inside the brackets
 * (#1809).
 */
const CaseFilterPickerDialog: React.FC<CaseFilterPickerDialogProps> = ({ open, onClose, onSave, customer, job }) => {
  const variableTree = useVariableTree({ customer, job, singleVariable: true, autoExpandVariables: true, loadCodes: true })
  // The variable last ticked. The tree hook refuses a hierarchic variable
  // without a word, so the picker says why once its code frames have loaded.
  const [tried, setTried] = useState<{ id: string; name: string } | null>(null)

  const triedIsHierarchic = !!tried && (variableTree.codes[tried.id] || []).some(c => c.type === 'Codeframe')
  const selected = variableTree.selectedVariables
  const frameTicked = selected.some(v => v.type === 'Codeframe')
  const variable = selected.find(v => v.type === 'Variable')
  const codes = (variable?.children || [])
    .filter(c => c.type === 'Code')
    .map(c => (c.value1 || '').trim())
    .filter(Boolean)
  const syntax = variable && codes.length > 0 ? `${(variable.value1 || '').trim()}(${codes.join(';')})` : ''

  const hierarchicMessage = triedIsHierarchic
    ? `${tried?.name} is hierarchic and can't be a case filter.`
    : frameTicked ? "This variable is hierarchic and can't be a case filter." : null
  const canSave = !!syntax && !frameTicked

  const handleCheckedToggle = (itemId: string, node: GenNode, isChecked: boolean) => {
    if (node.type === 'Variable' && isChecked) setTried({ id: getNodeID(node), name: node.value1 || '' })
    return variableTree.onNodeCheckedToggle(itemId, node, isChecked)
  }

  const handleClose = () => {
    setTried(null)
    onClose()
  }

  const handleSave = () => {
    if (!canSave) return
    onSave(syntax)
    variableTree.resetChecked()
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Choose a case filter</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          A case filter uses one variable only. Hierarchic variables can't be used.
        </Typography>
        {hierarchicMessage && <Alert severity="warning" sx={{ mb: 1 }}>{hierarchicMessage}</Alert>}
        <Box sx={{ height: 380, border: 1, borderColor: 'divider', borderRadius: 1, p: 1, overflow: 'hidden' }}>
          {variableTree.isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading variables…</Typography>
          ) : variableTree.isError ? (
            <Alert severity="error">Failed to load the variables.</Alert>
          ) : (
            <Tree
              searchEnabled
              showTooltips
              searchPlaceholder="Search variables..."
              nodes={variableTree.nodes}
              expanded={variableTree.expanded}
              getCheckboxState={variableTree.getCheckboxState}
              onNodeExpandToggle={variableTree.onNodeExpandToggle}
              onNodeCheckedToggle={handleCheckedToggle}
            />
          )}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5 }} color={syntax ? 'text.primary' : 'text.secondary'}>
          {syntax ? <>Case filter: <Box component="span" sx={{ fontFamily: 'monospace' }}>{syntax}</Box></> : 'Tick a variable and the codes to keep.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" color="info" onClick={handleSave} disabled={!canSave}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CaseFilterPickerDialog
